import React, { useState, useCallback, useEffect} from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, User } from 'lucide-react';
import DateTimePicker from './DateTimePicker';
import '../styles/FileUpload.css';

const FileUpload = ({ onFileSelect, onAnalysisComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [targetDateTime, setTargetDateTime] = useState(null);
  
  // Estat per a la informació de Strava
  const [stravaUser, setStravaUser] = useState(null);

  const STRAVA_CLIENT_ID = "193832";
  const authUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=https://api.tecnojs.com/api/strava/callback&response_type=code&scope=read,activity:read_all`;

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setError('Si us plau, puja un fitxer GPX vàlid');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB max
      setError('El fitxer és massa gran (màxim 10MB)');
      return false;
    }
    return true;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    setError(null);
    
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const savedStravaId = localStorage.getItem('strava_athlete_id');
      if (savedStravaId) {
        formData.append('strava_id', savedStravaId);
      }
      
      if (targetDateTime) {
        formData.append('target_datetime', targetDateTime);
      }

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/analyze`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error processant el fitxer');
      }

      const data = await response.json();
      
      setTimeout(() => {
        setUploading(false);
        onAnalysisComplete(data);
      }, 500);

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Error processant el fitxer GPX');
      setUploading(false);
      setProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setError(null);
    setProgress(0);
    setTargetDateTime(null);
  };
  
  const handleDisconnectStrava = () => {
    if (window.confirm('Segur que vols desconnectar el teu compte de Strava?')) {
      localStorage.removeItem('strava_athlete_id');
      localStorage.removeItem('strava_user_name');
      localStorage.removeItem('strava_user_avatar');
      setStravaUser(null);
    }
  };
  
  useEffect(() => {
    // Comprovar si ja hi ha dades de Strava guardades
    const savedId = localStorage.getItem('strava_athlete_id');
    const savedName = localStorage.getItem('strava_user_name');
    const savedAvatar = localStorage.getItem('strava_user_avatar');
    
    if (savedId && savedName) {
      setStravaUser({
        id: savedId,
        name: savedName,
        avatar: savedAvatar
      });
    }
    
    // Mira si la URL té els paràmetres de retorn de Strava
    const urlParams = new URLSearchParams(window.location.search);
    const stravaId = urlParams.get('strava_id');
    const stravaName = urlParams.get('strava_name');
    const stravaAvatar = urlParams.get('strava_avatar');
    
    if (stravaId && stravaName) {
      // Guardar al localStorage
      localStorage.setItem('strava_athlete_id', stravaId);
      localStorage.setItem('strava_user_name', stravaName);
      if (stravaAvatar) {
        localStorage.setItem('strava_user_avatar', stravaAvatar);
      }
      
      // Actualitzar l'estat
      setStravaUser({
        id: stravaId,
        name: stravaName,
        avatar: stravaAvatar
      });
      
      // Netejar la URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  return (
    <div className="file-upload-container">
      {/* SECCIÓ DEL BOTÓ DE STRAVA */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        {!stravaUser ? (
          <div>
            <a href={authUrl}>
              <button 
                className="strava-button" 
                style={{ 
                  backgroundColor: '#FC4C02', 
                  color: 'white', 
                  padding: '10px 20px', 
                  borderRadius: '5px', 
                  border: 'none', 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e34402'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#FC4C02'}
              >
                Connecta amb Strava
              </button>
            </a>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
              Accedeix als teus segments i estadístiques personals
            </p>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '10px',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            {stravaUser.avatar ? (
              <img 
                src={stravaUser.avatar} 
                alt="Strava Avatar" 
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%',
                  border: '2px solid #FC4C02'
                }} 
              />
            ) : (
              <User size={40} style={{ color: '#FC4C02' }} />
            )}
            <div style={{ textAlign: 'left' }}>
              <div style={{ 
                color: '#FC4C02', 
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <CheckCircle size={18} /> 
                Connectat amb Strava
              </div>
              <div style={{ fontSize: '0.9rem', color: '#333' }}>
                {stravaUser.name}
              </div>
            </div>
            <button
              onClick={handleDisconnectStrava}
              style={{
                marginLeft: '10px',
                padding: '5px 10px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
            >
              Desconnectar
            </button>
          </div>
        )}
      </div>

      {!selectedFile ? (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload size={48} className="upload-icon" />
          <h3>Puja el teu fitxer GPX</h3>
          <p>Arrossega i deixa anar o fes clic per seleccionar</p>
          
          <input
            type="file"
            id="file-input"
            className="file-input"
            accept=".gpx"
            onChange={handleChange}
          />
          <label htmlFor="file-input" className="upload-button">
            Selecciona fitxer
          </label>

          <div className="file-info">
            <p>Formats acceptats: .gpx</p>
            <p>Mida màxima: 10MB</p>
          </div>
        </div>
      ) : (
        <div className="file-selected">
          <div className="file-details">
            <FileText size={40} className="file-icon" />
            <div className="file-info-text">
              <h4>{selectedFile.name}</h4>
              <p>{(selectedFile.size / 1024).toFixed(2)} KB</p>
            </div>
            {!uploading && (
              <button onClick={handleReset} className="reset-button">
                ✕
              </button>
            )}
          </div>

          {!uploading && (
            <>
              <DateTimePicker 
                value={targetDateTime}
                onChange={setTargetDateTime}
              />
              
              <button onClick={handleUpload} className="analyze-button">
                Analitzar Ruta
              </button>
            </>
          )}

          {uploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="upload-status">Analitzant ruta...</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;